import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ProcessExecutionResponse,
  StepExecutionResponse,
} from "api/main/data-contracts";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Drawer,
  Hidden,
  List,
  ListItem,
  ListItemText,
  Typography,
  Card,
  Chip,
  LinearProgress,
  Grid,
} from "@mui/material";
import StepExecution from "pages/processexecution/execution/StepExecution";
import { useAppSelector } from "redux/hooks";
import { hasRole } from "util/UtilityFunctions";
import { RoleNames } from "models/auth/RoleNames";
import InputIcon from "@mui/icons-material/Input";
import OutputIcon from "@mui/icons-material/Publish";
import GenerateDocumentIcon from "@mui/icons-material/Description";
import ConfirmationIcon from "@mui/icons-material/CheckCircle";
import PaymentIcon from "@mui/icons-material/Payment";
import NotificationIcon from "@mui/icons-material/Notifications";
import SignIcon from "@mui/icons-material/Create";
import IdentificationIcon from "@mui/icons-material/Badge";
import { createApi } from "util/ApiCreator";
import { ProcessExecutionPublicController } from "api/main/ProcessExecutionPublicController";
import { ProcessExecutionAdminController } from "api/main/ProcessExecutionAdminController";
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { adminTheme, statusColors } from "theme/adminTheme";

const ProcessExecutionPage = () => {
  const [backdropOpen, setBackdropOpen] = useState<boolean>(false);

  const { id, stepIndex } = useParams();
  const [currentStepId, setCurrentStepId] = useState<number>(
    stepIndex ? +stepIndex : 0,
  );
  const common = useAppSelector((state) => state.common);

  const me = useAppSelector((s) => s.me.info);
  const navigate = useNavigate();
  const location = useLocation();
  const api = useRef(createApi("processexecution") as ProcessExecutionPublicController);
  const adminApi = useRef(createApi("processexecutionadmin") as ProcessExecutionAdminController);

  const [processExecution, setProcess] = useState<ProcessExecutionResponse>();
  const [currentStep, setCurrentStep] = useState<StepExecutionResponse>();

  const [adminTurnOffer, setAdminTurnOffer] = useState<boolean>(false);
  const [adminTurnSign, setAdminTurnSign] = useState<boolean>(false);

  const [open, setOpen] = useState<boolean>(false);

  const [canGoBack, setCanGoBack] = useState<boolean>(false);
  const [canGoNext, setCanGoNext] = useState<boolean>(false);
  const [showDialogOnce, setShowDialogOnce] = useState<boolean>(false);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, location.pathname]);

  const getData = async () => {
    setBackdropOpen(true);

    if (id && stepIndex) {
      let res;
      if (hasRole(RoleNames.ADMIN, me?.roles)) {
        res = await adminApi.current.findById(+id);
      } else {
        res = await api.current.findById(+id);
      }

      setProcess(res);

      setCanGoBack(+stepIndex !== 0);

      if (res?.stepExecutions && res.stepExecutions.length >= +stepIndex) {
        setCurrentStep(res.stepExecutions[+stepIndex]);
        setCanGoNext(+stepIndex + 1 < res?.stepExecutions?.length);
      }
      setBackdropOpen(false);

      let active = res?.stepExecutions?.find(step => step.status === 'ACTIVE');
      if (!active)
        return;

      let isAdminStep = active?.step?.executors?.includes("ADMIN");
      if (!isAdminStep)
        return;

      if (active.step?.type === "DATA_INPUT") {
        setAdminTurnOffer(true);
      }

      if (active.step?.type === "SIGN") {
        // Only show the modal if the user has actually signed their part
        // Check if there's a completed USER SIGN step before this admin SIGN step
        const userSignStepCompleted = res?.stepExecutions?.some(
          (step) =>
            step.step?.executors?.includes("USER") &&
            step.step?.type === "SIGN" &&
            step.status === "FINISHED" &&
            step.executionInfo?.signingInfo?.signed === true
        );

        if (userSignStepCompleted) {
          setAdminTurnSign(true);
        }
      }
    }
  };

  useEffect(() => {
    if (showDialogOnce || hasRole(RoleNames.ADMIN, me?.roles)) {
      return
    }
    setOpen(adminTurnOffer || adminTurnSign || processExecution?.status === "FINISHED");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTurnOffer, adminTurnSign, processExecution])

  const next = (goToId?: number) => {
    let nextStepId = Number.isInteger(goToId)
      ? Number(goToId)
      : currentStepId + 1;

    if (
      processExecution?.stepExecutions &&
      processExecution?.stepExecutions?.length >= nextStepId
    ) {
      navigate(`/process/execution/${id}/${nextStepId}`);
      setCurrentStepId(nextStepId);
    } else {
      getData();
    }
  };

  const previous = () => {
    let nextStepId = currentStepId - 1;

    if (
      processExecution?.stepExecutions &&
      processExecution?.stepExecutions?.length >= nextStepId &&
      nextStepId >= 0
    ) {
      navigate(`/process/execution/${id}/${nextStepId}`);
      setCurrentStepId(nextStepId);
    } else {
      getData();
    }
  };

  function getCurrentTitle(currentStep: StepExecutionResponse) {
    if (currentStep.step?.description) {
      return currentStep.step.description;
    }

    if (currentStep.type === "SIGN") {
      if (currentStep.step?.stepInformation?.signContent?.signType === "QUALIFIED_SIGNATURE") {
        return "";
      }
      return "";
    }

    if (currentStep.type === "DATA_INPUT") {
      if (currentStep.name === "Ačiū. Laukite atsakymo.")
        return "Ačiū už pateiktą informaciją ir dokumentus."
      return "Šiame lange jūs turite užpildyti reikalingą informaciją bei įkelti reikiamus dokumentus."
    }

    if (currentStep.type === "PAYMENT") {
      if (currentStep.status === "FINISHED") return "Jūs jau sumokėjote";

      if (currentStep.status === "NOT_ACTIVE")
        return "Prašome atlikti mokėjimą";

      if (currentStep.status === "ACTIVE") return "Prašome atlikti mokėjimą";
    }

    if (currentStep.type === "IDENTIFICATION") {
      return "Prašome patvirtinti jūsų tapatybę, siekiant identifikuoti asmenį, su kuriuo yra bendraujama, bei tinkamai parengti dokumentus."
    }

    if (currentStep.type === "CONFIRMATION")
      return "Administratorius pateikė jums pasiūlymą. Prašome peržiūrėti ir patvirtinti pateiktą informaciją";
  }

  function getIcon(type: any, isActive: boolean = false) {
    const iconStyle = {
      fontSize: 20,
      color: isActive ? adminTheme.colors.primary : adminTheme.colors.textMuted
    };

    switch (type) {
      case "DATA_INPUT":
        return <InputIcon sx={iconStyle} />;
      case "DATA_OUTPUT":
        return <OutputIcon sx={iconStyle} />;
      case "GENERATE_DOCUMENT":
        return <GenerateDocumentIcon sx={iconStyle} />;
      case "CONFIRMATION":
        return <ConfirmationIcon sx={iconStyle} />;
      case "PAYMENT":
        return <PaymentIcon sx={iconStyle} />;
      case "NOTIFICATION":
        return <NotificationIcon sx={iconStyle} />;
      case "SIGN":
        return <SignIcon sx={iconStyle} />;
      case "IDENTIFICATION":
        return <IdentificationIcon sx={iconStyle} />;
      default:
        return <InputIcon sx={iconStyle} />;
    }
  }

  function getByLabelText(currentStep?: StepExecutionResponse): string {
    if (currentStep?.type === "DATA_INPUT") {
      return currentStep?.status === "FINISHED" ? "Pateikta" : "Laukiama";
    }
    if (currentStep?.type === "SIGN") {
      if (currentStep.step?.stepInformation?.signContent?.signType === "QUALIFIED_SIGNATURE") {
        return currentStep?.status === "FINISHED" ? "Pasirašyta" : "Laukiama";
      }
      return currentStep?.status === "FINISHED" ? "Susipažinta" : "Laukiama";

    }
    if (currentStep?.type === "PAYMENT") {
      return currentStep?.status === "FINISHED" ? "Apmokėta" : "Laukiama";
    }

    if (currentStep?.type === "IDENTIFICATION") {
      return currentStep?.status === "FINISHED" ? "Identifikacija atlikta" : "Laukiama";
    }

    if (currentStep?.type === "CONFIRMATION") {
      return currentStep?.status === "FINISHED" ? "Patvirtinta" : "Laukiama";
    }

    return currentStep?.status === "FINISHED" ? "Atlikta" : "Laukiama";
  }

  function handleClose() {
    setShowDialogOnce(true);
    setOpen(false);
  }

  function getStepStatusInfo(step: StepExecutionResponse) {
    const stepIsForUser = step?.step?.executors?.includes("USER");
    const stepIsForAdmin = step?.step?.executors?.includes("ADMIN");
    const currentUserIsAdmin = hasRole(RoleNames.ADMIN, me?.roles);

    // Determine the label based on current user's role and step executor
    let executorLabel = "";
    if (currentUserIsAdmin) {
      // Admin viewing
      if (stepIsForAdmin) {
        executorLabel = "(jūsų)";
      } else if (stepIsForUser) {
        executorLabel = "(vartotojo)";
      }
    } else {
      // User viewing
      if (stepIsForUser) {
        executorLabel = "(jūsų)";
      } else if (stepIsForAdmin) {
        executorLabel = "(administratoriaus)";
      }
    }

    if (step.status === "ACTIVE") {
      return {
        text: `Aktyvus ${executorLabel}`,
        color: adminTheme.colors.primary,
        icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} />,
      };
    }
    if (step.status === "NOT_ACTIVE") {
      return {
        text: `Laukia ${executorLabel}`,
        color: adminTheme.colors.textMuted,
        icon: <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />,
      };
    }
    if (step.status === "FINISHED") {
      return {
        text: `Atlikta ${executorLabel}`,
        color: adminTheme.colors.success,
        icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
      };
    }
    return { text: "", color: adminTheme.colors.textMuted, icon: null };
  }

  // Calculate progress percentage
  const completedSteps = processExecution?.stepExecutions?.filter(s => s.status === "FINISHED").length || 0;
  const totalSteps = processExecution?.stepExecutions?.length || 1;
  const progressPercentage = Math.round((completedSteps / totalSteps) * 100);

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
      {/* Loading indicator */}
      {backdropOpen && <LinearProgress sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999 }} />}

      <Box sx={{ display: "flex", minHeight: "100%" }}>
        {/* Main Content */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {currentStep && (
            <Box sx={{ p: 3, flex: 1, width: "100%", boxSizing: "border-box" }}>
              {/* Page Header */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{
                      borderColor: adminTheme.colors.border,
                      color: adminTheme.colors.textSecondary,
                      "&:hover": {
                        borderColor: adminTheme.colors.primary,
                        bgcolor: `${adminTheme.colors.primary}08`,
                        color: adminTheme.colors.primary,
                      },
                    }}
                  >
                    Atgal
                  </Button>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: adminTheme.colors.textPrimary,
                  }}
                >
                  {processExecution?.process?.name || "Proceso vykdymas"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: adminTheme.colors.textSecondary }}
                >
                  {processExecution?.status === "FINISHED"
                    ? "Procesas sėkmingai užbaigtas"
                    : "Sekite žingsnius, kad užbaigtumėte procesą"}
                </Typography>
              </Box>

              {/* Progress Stats - Hidden for SIGN step type */}
              {currentStep.type !== "SIGN" && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{
                        p: 3,
                        borderRadius: adminTheme.borderRadius.lg,
                        border: `1px solid ${adminTheme.colors.border}`,
                        boxShadow: "none",
                        transition: adminTheme.transitions.normal,
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: adminTheme.shadows.cardHover,
                          borderColor: "transparent",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary, fontWeight: 500 }}>
                          Progresas
                        </Typography>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: adminTheme.borderRadius.md,
                            background: adminTheme.gradients.primary,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <PlayArrowIcon sx={{ fontSize: 24 }} />
                        </Box>
                      </Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: adminTheme.colors.textPrimary }}>
                        {progressPercentage}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={progressPercentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: adminTheme.colors.borderLight,
                          "& .MuiLinearProgress-bar": {
                            background: adminTheme.gradients.primary,
                            borderRadius: 4,
                          },
                        }}
                      />
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{
                        p: 3,
                        borderRadius: adminTheme.borderRadius.lg,
                        border: `1px solid ${adminTheme.colors.border}`,
                        boxShadow: "none",
                        transition: adminTheme.transitions.normal,
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: adminTheme.shadows.cardHover,
                          borderColor: "transparent",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary, fontWeight: 500 }}>
                          Atlikti žingsniai
                        </Typography>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: adminTheme.borderRadius.md,
                            background: adminTheme.gradients.success,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <CheckCircleOutlineIcon sx={{ fontSize: 24 }} />
                        </Box>
                      </Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: adminTheme.colors.textPrimary }}>
                        {completedSteps}
                      </Typography>
                      <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
                        iš {totalSteps} žingsnių
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{
                        p: 3,
                        borderRadius: adminTheme.borderRadius.lg,
                        border: `1px solid ${adminTheme.colors.border}`,
                        boxShadow: "none",
                        transition: adminTheme.transitions.normal,
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: adminTheme.shadows.cardHover,
                          borderColor: "transparent",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary, fontWeight: 500 }}>
                          Dabartinis žingsnis
                        </Typography>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: adminTheme.borderRadius.md,
                            background: adminTheme.gradients.warning,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <HourglassEmptyIcon sx={{ fontSize: 24 }} />
                        </Box>
                      </Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: adminTheme.colors.textPrimary }}>
                        {currentStepId + 1}
                      </Typography>
                      <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
                        {currentStep.name}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{
                        p: 3,
                        borderRadius: adminTheme.borderRadius.lg,
                        border: `1px solid ${adminTheme.colors.border}`,
                        boxShadow: "none",
                        transition: adminTheme.transitions.normal,
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: adminTheme.shadows.cardHover,
                          borderColor: "transparent",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary, fontWeight: 500 }}>
                          Būsena
                        </Typography>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: adminTheme.borderRadius.md,
                            bgcolor: processExecution?.status === "FINISHED"
                              ? `${adminTheme.colors.success}15`
                              : `${adminTheme.colors.primary}15`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: processExecution?.status === "FINISHED"
                              ? adminTheme.colors.success
                              : adminTheme.colors.primary,
                          }}
                        >
                          {processExecution?.status === "FINISHED"
                            ? <CheckCircleOutlineIcon sx={{ fontSize: 24 }} />
                            : <HourglassEmptyIcon sx={{ fontSize: 24 }} />
                          }
                        </Box>
                      </Box>
                      <Chip
                        label={processExecution?.status === "FINISHED" ? "Užbaigtas" : "Vykdomas"}
                        sx={{
                          bgcolor: processExecution?.status === "FINISHED"
                            ? statusColors.finished?.bg || "rgba(16, 185, 129, 0.1)"
                            : statusColors.in_progress?.bg || "rgba(99, 102, 241, 0.1)",
                          color: processExecution?.status === "FINISHED"
                            ? statusColors.finished?.text || adminTheme.colors.success
                            : statusColors.in_progress?.text || adminTheme.colors.primary,
                          fontWeight: 600,
                          mt: 1,
                        }}
                      />
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Current Step Card */}
              <Card
                sx={{
                  borderRadius: adminTheme.borderRadius.lg,
                  border: `1px solid ${adminTheme.colors.border}`,
                  boxShadow: "none",
                  mb: 3,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    background: `linear-gradient(135deg, ${adminTheme.colors.primary}08 0%, ${adminTheme.colors.background} 100%)`,
                    borderBottom: `1px solid ${adminTheme.colors.border}`,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 300 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: adminTheme.borderRadius.md,
                            background: adminTheme.gradients.primary,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          {getIcon(currentStep.type, true)}
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: adminTheme.colors.textMuted, letterSpacing: 1.5, fontWeight: 600, textTransform: "uppercase" }}
                          >
                            Žingsnis {currentStepId + 1} iš {processExecution?.stepExecutions?.length || 0}
                          </Typography>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              color: adminTheme.colors.textPrimary,
                            }}
                          >
                            {currentStep.name}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Chip
                      label={getByLabelText(currentStep)}
                      sx={{
                        bgcolor: currentStep.status === "FINISHED"
                          ? "rgba(16, 185, 129, 0.1)"
                          : "rgba(245, 158, 11, 0.1)",
                        color: currentStep.status === "FINISHED"
                          ? adminTheme.colors.success
                          : adminTheme.colors.warning,
                        fontWeight: 600,
                        px: 2,
                        py: 0.5,
                        fontSize: "0.875rem",
                      }}
                    />
                  </Box>
                </Box>
                <Hidden mdDown>
                  <Box sx={{ p: 3, bgcolor: adminTheme.colors.background }}>
                    <Typography
                      variant="body2"
                      sx={{ color: adminTheme.colors.textSecondary, lineHeight: 1.7 }}
                    >
                      {getCurrentTitle(currentStep)}
                    </Typography>
                  </Box>
                </Hidden>
              </Card>

              {/* Step Execution Content */}
              <Card
                sx={{
                  borderRadius: adminTheme.borderRadius.lg,
                  border: `1px solid ${adminTheme.colors.border}`,
                  boxShadow: "none",
                  p: 3,
                  ...(currentStep.type === "SIGN" && {
                    height: "calc(100vh - 280px)",
                    minHeight: "600px",
                  }),
                }}
              >
                <StepExecution
                  step={currentStep}
                  process={processExecution}
                  refresh={getData}
                  next={next}
                  previous={previous}
                  canGoBack={canGoBack}
                  canGoNext={canGoNext}
                />
              </Card>
            </Box>
          )}
        </Box>

        {/* Right Sidebar */}
        {common.sideMenuIsPermanent && (
          <Drawer
            variant="permanent"
            anchor="right"
            sx={{
              "& .MuiDrawer-paper": {
                width: "340px",
                zIndex: "0",
                height: "calc(100% - 80px)",
                marginTop: "80px",
                bgcolor: adminTheme.colors.background,
                borderLeft: `1px solid ${adminTheme.colors.border}`,
              },
            }}
          >
            <Box sx={{ p: 3, borderBottom: `1px solid ${adminTheme.colors.border}` }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary, mb: 0.5 }}>
                Proceso žingsniai
              </Typography>
              <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
                {processExecution?.process?.name}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
                    Progresas
                  </Typography>
                  <Typography variant="caption" sx={{ color: adminTheme.colors.textPrimary, fontWeight: 600 }}>
                    {completedSteps}/{totalSteps}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: adminTheme.colors.borderLight,
                    "& .MuiLinearProgress-bar": {
                      background: adminTheme.gradients.primary,
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
              <List disablePadding>
                {processExecution?.stepExecutions?.map(
                  (step: StepExecutionResponse, listIndex: number) => {
                    const statusInfo = getStepStatusInfo(step);
                    const isCurrentStep = currentStepId === listIndex;

                    return (
                      <ListItem
                        key={listIndex}
                        onClick={() => next(listIndex)}
                        sx={{
                          cursor: "pointer",
                          borderRadius: adminTheme.borderRadius.md,
                          mb: 1.5,
                          p: 2,
                          bgcolor: isCurrentStep
                            ? "rgba(99, 102, 241, 0.08)"
                            : adminTheme.colors.background,
                          border: isCurrentStep
                            ? `2px solid ${adminTheme.colors.primary}`
                            : `1px solid ${adminTheme.colors.border}`,
                          transition: adminTheme.transitions.normal,
                          "&:hover": {
                            transform: "translateX(-4px)",
                            bgcolor: isCurrentStep
                              ? "rgba(99, 102, 241, 0.12)"
                              : "rgba(99, 102, 241, 0.04)",
                            borderColor: adminTheme.colors.primary,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: adminTheme.borderRadius.sm,
                            bgcolor: step.status === "FINISHED"
                              ? "rgba(16, 185, 129, 0.1)"
                              : isCurrentStep
                                ? "rgba(99, 102, 241, 0.15)"
                                : adminTheme.colors.backgroundAlt,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                            color: step.status === "FINISHED"
                              ? adminTheme.colors.success
                              : isCurrentStep
                                ? adminTheme.colors.primary
                                : adminTheme.colors.textMuted,
                          }}
                        >
                          {step.status === "FINISHED"
                            ? <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                            : getIcon(step.type, isCurrentStep)
                          }
                        </Box>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: isCurrentStep ? 600 : 500,
                                color: isCurrentStep
                                  ? adminTheme.colors.textPrimary
                                  : adminTheme.colors.textSecondary,
                                mb: 0.5,
                              }}
                            >
                              {step.name}
                            </Typography>
                          }
                          secondary={
                            <Chip
                              label={statusInfo.text}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: "0.7rem",
                                bgcolor: step.status === "FINISHED"
                                  ? "rgba(16, 185, 129, 0.1)"
                                  : step.status === "ACTIVE"
                                    ? "rgba(99, 102, 241, 0.1)"
                                    : "rgba(148, 163, 184, 0.1)",
                                color: statusInfo.color,
                                fontWeight: 600,
                              }}
                            />
                          }
                          secondaryTypographyProps={{ component: "div" }}
                        />
                      </ListItem>
                    );
                  },
                )}
              </List>
            </Box>
          </Drawer>
        )}
      </Box>

      {/* Info Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: adminTheme.borderRadius.lg,
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ p: 5, textAlign: "center" }}>
          {adminTurnOffer && (
            <>
              <Box
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${adminTheme.colors.primary}20 0%, ${adminTheme.colors.primary}10 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <ThumbUpAltIcon sx={{ fontSize: 48, color: adminTheme.colors.primary }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: adminTheme.colors.textPrimary }}>
                Laukite administratoriaus atsakymo
              </Typography>
              <Typography sx={{ color: adminTheme.colors.textSecondary, lineHeight: 1.8 }}>
                Ačiū už pateiktus dokumentus. Dabar sulaukite atsakymo. Administratorius, peržiūrėjęs visus jūsų pateiktus duomenis, įvertins jūsų situaciją. Jeigu jūsų pateiktos informacijos neužteks, administratorius susisieks su jumis ir paprašys patikslinti ar pateikti papildomą informaciją.
              </Typography>
            </>
          )}

          {adminTurnSign && (
            <>
              <Box
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${adminTheme.colors.primary}20 0%, ${adminTheme.colors.primary}10 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <ThumbUpAltIcon sx={{ fontSize: 48, color: adminTheme.colors.primary }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: adminTheme.colors.textPrimary }}>
                Jūs baigėte pildyti visus dokumentus
              </Typography>
              <Typography sx={{ color: adminTheme.colors.textSecondary, lineHeight: 1.8 }}>
                Administratorius turi pasirašyti dokumentus, kad procesas būtų užbaigtas. Apie pasirašytus dokumentus informuosime atskirai.
                Kylant neaiškumams prašome susisiekti el. paštu: <a href="mailto:info@recodinai.lt" style={{ color: adminTheme.colors.primary }}>info@recodinai.lt</a>.
              </Typography>
            </>
          )}

          {processExecution?.status === "FINISHED" && !adminTurnOffer && !adminTurnSign && (
            <>
              <Box
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${adminTheme.colors.success}20 0%, ${adminTheme.colors.success}10 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <CheckCircleOutlineIcon sx={{ fontSize: 48, color: adminTheme.colors.success }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: adminTheme.colors.textPrimary }}>
                Visi dokumentai yra pasirašyti
              </Typography>
              <Typography sx={{ color: adminTheme.colors.textSecondary, lineHeight: 1.8 }}>
                Procesas sėkmingai užbaigtas. Apie tolimesnes naujienas informuosime atskirai. Kylant neaiškumams prašome susisiekti el. paštu: <a href="mailto:info@recodinai.lt" style={{ color: adminTheme.colors.primary }}>info@recodinai.lt</a>.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, justifyContent: "center" }}>
          <Button
            size="large"
            variant="contained"
            onClick={handleClose}
            sx={{
              background: adminTheme.gradients.primary,
              "&:hover": { background: adminTheme.gradients.primaryHover },
              px: 5,
              py: 1.5,
              fontWeight: 600,
            }}
          >
            Tęsti toliau
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={backdropOpen}
      >
        <Card sx={{ p: 4, textAlign: "center", borderRadius: adminTheme.borderRadius.lg }}>
          <CircularProgress sx={{ color: adminTheme.colors.primary, mb: 2 }} />
          <Typography sx={{ color: adminTheme.colors.textPrimary, fontWeight: 500 }}>Kraunama...</Typography>
        </Card>
      </Backdrop>
    </Box>
  );
};

export default ProcessExecutionPage;
